import { useState, useEffect, useCallback } from "react";
import QMSLayout from "../components/QMSLayout";
import { API } from "../App";
import { useTranslation } from "react-i18next";
import {
  Shield, FileText, Download, CheckCircle2, XCircle, Lock,
  Loader2, AlertTriangle, Fingerprint, PenTool, RefreshCw,
  ChevronDown, ChevronRight, Eye, Clock, FileCheck
} from "lucide-react";

const SDVRPage = () => {
  const { t } = useTranslation('platform');
  const [analyses, setAnalyses] = useState([]);
  const [sdvrReports, setSdvrReports] = useState([]);
  const [verification, setVerification] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [signing, setSigning] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [expandedVolume, setExpandedVolume] = useState(null);
  const [signMeaning, setSignMeaning] = useState("I have reviewed and approve this SDVR report");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [aRes, rRes] = await Promise.all([
        fetch(`${API}/sentinel/analyses?limit=50`, { credentials: "include" }),
        fetch(`${API}/sentinel/reports/sdvr?limit=50`, { credentials: "include" }),
      ]);
      if (aRes.ok) { const d = await aRes.json(); setAnalyses(d.analyses || []); }
      if (rRes.ok) { const d = await rRes.json(); setSdvrReports(d.reports || []); }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const runVerification = async () => {
    setVerifying(true);
    try {
      const res = await fetch(`${API}/sentinel/algorithm-verification`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setVerification(data);
      }
    } catch (e) { console.error(e); }
    setVerifying(false);
  };

  const generateSDVR = async (analysisId) => {
    setGenerating(true);
    try {
      const res = await fetch(`${API}/sentinel/reports/generate-sdvr`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ analysis_id: analysisId }),
      });
      if (res.ok) {
        await fetchData();
      } else {
        const err = await res.json();
        alert(err.detail || "Failed to generate SDVR");
      }
    } catch (e) { console.error(e); }
    setGenerating(false);
  };

  const downloadPDF = async (reportId) => {
    try {
      const res = await fetch(`${API}/sentinel/reports/sdvr/${reportId}/pdf`, { credentials: "include" });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${reportId}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (e) { console.error(e); }
  };

  const signReport = async (reportId) => {
    setSigning(true);
    try {
      const res = await fetch(`${API}/sentinel/reports/sdvr/${reportId}/sign`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ meaning: signMeaning }),
      });
      if (res.ok) {
        await fetchData();
        setSelectedReport(null);
      } else {
        const err = await res.json();
        alert(err.detail || "Failed to sign report");
      }
    } catch (e) { console.error(e); }
    setSigning(false);
  };

  const viewReport = async (reportId) => {
    try {
      const res = await fetch(`${API}/sentinel/reports/sdvr/${reportId}`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setSelectedReport(data);
      }
    } catch (e) { console.error(e); }
  };

  const StatusBadge = ({ status }) => {
    const styles = {
      locked: { bg: "bg-red-500/10", border: "border-red-500/30", text: "text-red-400", icon: Lock, label: "LOCKED" },
      ready_for_signature: { bg: "bg-cyan-500/10", border: "border-cyan-500/30", text: "text-cyan-400", icon: PenTool, label: "READY FOR SIGNATURE" },
      signed: { bg: "bg-emerald-500/10", border: "border-emerald-500/30", text: "text-emerald-400", icon: CheckCircle2, label: "SIGNED" },
    };
    const s = styles[status] || styles.locked;
    const Icon = s.icon;
    return (
      <span data-testid={`sdvr-status-${status}`} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-[3px] text-xs font-bold tracking-wide ${s.bg} ${s.border} border ${s.text}`}>
        <Icon className="w-3.5 h-3.5" /> {s.label}
      </span>
    );
  };

  const VerificationCard = () => (
    <div className="bg-[#132340] border border-[#1E3A5F] rounded-[3px] p-6" data-testid="algorithm-verification-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Fingerprint className="w-5 h-5 text-[#00C8E0]" />
          <h3 className="text-white font-semibold text-sm tracking-wide uppercase" style={{ fontFamily: "'Syne', sans-serif" }}>
            NIST Algorithm Verification
          </h3>
        </div>
        <button
          data-testid="run-verification-btn"
          onClick={runVerification}
          disabled={verifying}
          className="flex items-center gap-2 px-4 py-2 bg-[#1A6BCC] text-white text-xs font-bold uppercase tracking-wide rounded-[3px] hover:shadow-[0_0_20px_rgba(0,200,224,0.3)] transition-all disabled:opacity-50"
        >
          {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          {verifying ? "Validating..." : "Run Verification"}
        </button>
      </div>

      {verification && (
        <div className="space-y-3">
          <div className={`flex items-center gap-3 p-3 rounded-[3px] border ${verification.validation_status === "PASS" ? "bg-emerald-500/10 border-emerald-500/30" : "bg-red-500/10 border-red-500/30"}`}>
            {verification.validation_status === "PASS"
              ? <CheckCircle2 className="w-5 h-5 text-emerald-400" />
              : <XCircle className="w-5 h-5 text-red-400" />}
            <div>
              <span data-testid="verification-status" className={`font-bold text-sm ${verification.validation_status === "PASS" ? "text-emerald-400" : "text-red-400"}`}>
                {verification.validation_status}
              </span>
              <span className="text-slate-400 text-xs ml-3">
                {verification.datasets_validated} datasets validated | Threshold: {verification.threshold_pct}%
              </span>
            </div>
          </div>

          {verification.results?.map((r, i) => (
            <div key={i} className={`p-3 rounded-[3px] border text-xs ${r.overall_pass ? "bg-[#0F1E35] border-[#1E3A5F]" : "bg-red-500/5 border-red-500/20"}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-slate-300 font-medium">{r.description}</span>
                <span className={`font-bold ${r.overall_pass ? "text-emerald-400" : "text-red-400"}`}>
                  {r.overall_pass ? "PASS" : "FAIL"}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-slate-400 mt-1">
                <span>Mean: {r.mean.calculated} (cert: {r.mean.certified}) — {r.mean.variance_pct.toFixed(8)}%</span>
                <span>StdDev: {r.std_dev.calculated} (cert: {r.std_dev.certified}) — {r.std_dev.variance_pct.toFixed(8)}%</span>
                {r.cpk && <span className="col-span-2">Cpk: {r.cpk.calculated} (cert: {r.cpk.certified}) — {r.cpk.variance_pct.toFixed(8)}%</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {!verification && !verifying && (
        <p className="text-slate-500 text-xs">Run Algorithm Verification to compare Sentinel engine outputs against NIST Gold Standard reference datasets.</p>
      )}
    </div>
  );

  if (loading) {
    return (
      <QMSLayout title={t('platform:improvement.title')}>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#00C8E0]" />
        </div>
      </QMSLayout>
    );
  }

  return (
    <QMSLayout title={t('platform:statExplainer.title')}>
      <div className="space-y-6" data-testid="sdvr-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white tracking-tight" style={{ fontFamily: "'Syne', sans-serif" }}>
              SDVR — Validation Reports
            </h1>
            <p className="text-slate-400 text-sm mt-1">
              QMSR-compliant Statistical & Data Validation Reports with NIST Gold Standard verification
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500 bg-[#0F1E35] px-3 py-1.5 rounded-[3px] border border-[#1E3A5F]">
              Build: SENTINEL-SDVR-001
            </span>
          </div>
        </div>

        {/* Algorithm Verification Panel */}
        <VerificationCard />

        {/* Generate SDVR from Analyses */}
        <div className="bg-[#132340] border border-[#1E3A5F] rounded-[3px] p-6" data-testid="generate-sdvr-panel">
          <div className="flex items-center gap-3 mb-4">
            <FileCheck className="w-5 h-5 text-[#00C8E0]" />
            <h3 className="text-white font-semibold text-sm tracking-wide uppercase" style={{ fontFamily: "'Syne', sans-serif" }}>
              Generate SDVR from Analysis
            </h3>
          </div>

          {analyses.length === 0 ? (
            <p className="text-slate-500 text-xs">No analyses found. Upload data and run analysis in Sentinel Analytics first.</p>
          ) : (
            <div className="space-y-2">
              {analyses.slice(0, 10).map((a) => {
                const hasSDVR = sdvrReports.some((r) => r.analysis_id === a.analysis_id);
                return (
                  <div key={a.analysis_id} className="flex items-center justify-between p-3 bg-[#0F1E35] border border-[#1E3A5F] rounded-[3px]">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-white text-sm font-medium truncate">{a.analysis_name || a.column}</span>
                        <span className="text-slate-500 text-xs">{a.analysis_id}</span>
                      </div>
                      <div className="text-slate-400 text-xs mt-0.5">
                        {a.data_points || "?"} points | {new Date(a.analyzed_at).toLocaleString()}
                      </div>
                    </div>
                    {hasSDVR ? (
                      <span className="text-emerald-400 text-xs font-medium flex items-center gap-1">
                        <CheckCircle2 className="w-3.5 h-3.5" /> SDVR Generated
                      </span>
                    ) : (
                      <button
                        data-testid={`generate-sdvr-${a.analysis_id}`}
                        onClick={() => generateSDVR(a.analysis_id)}
                        disabled={generating}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-[#1A6BCC] text-white text-xs font-bold uppercase tracking-wide rounded-[3px] hover:shadow-[0_0_20px_rgba(0,200,224,0.3)] transition-all disabled:opacity-50"
                      >
                        {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />}
                        Generate SDVR
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* SDVR Reports List */}
        <div className="bg-[#132340] border border-[#1E3A5F] rounded-[3px] p-6" data-testid="sdvr-reports-list">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="w-5 h-5 text-[#00C8E0]" />
            <h3 className="text-white font-semibold text-sm tracking-wide uppercase" style={{ fontFamily: "'Syne', sans-serif" }}>
              SDVR Reports ({sdvrReports.length})
            </h3>
          </div>

          {sdvrReports.length === 0 ? (
            <p className="text-slate-500 text-xs">No SDVR reports generated yet.</p>
          ) : (
            <div className="space-y-2">
              {sdvrReports.map((r) => (
                <div key={r.report_id} className="flex items-center justify-between p-3 bg-[#0F1E35] border border-[#1E3A5F] rounded-[3px]">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-white text-sm font-medium font-mono">{r.report_id}</span>
                      <StatusBadge status={r.status} />
                    </div>
                    <div className="text-slate-400 text-xs mt-0.5 flex items-center gap-3">
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{new Date(r.generated_at).toLocaleString()}</span>
                      <span>Analysis: {r.analysis_id}</span>
                      {r.validation_status === "PASS"
                        ? <span className="text-emerald-400 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />NIST Verified</span>
                        : <span className="text-red-400 flex items-center gap-1"><XCircle className="w-3 h-3" />NIST FAILED</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      data-testid={`view-sdvr-${r.report_id}`}
                      onClick={() => viewReport(r.report_id)}
                      className="p-2 text-slate-400 hover:text-[#00C8E0] transition-colors"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    {r.pdf_generated && (
                      <button
                        data-testid={`download-sdvr-${r.report_id}`}
                        onClick={() => downloadPDF(r.report_id)}
                        className="p-2 text-slate-400 hover:text-[#00C8E0] transition-colors"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                    )}
                    {!r.report_locked && r.status !== "signed" && (
                      <button
                        data-testid={`sign-sdvr-${r.report_id}`}
                        onClick={() => viewReport(r.report_id)}
                        className="flex items-center gap-1 px-3 py-1.5 bg-emerald-600 text-white text-xs font-bold uppercase tracking-wide rounded-[3px] hover:bg-emerald-500 transition-colors"
                      >
                        <PenTool className="w-3.5 h-3.5" /> Sign
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Report Detail Modal */}
        {selectedReport && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setSelectedReport(null)}>
            <div className="bg-[#0F1E35] border border-[#1E3A5F] rounded-[3px] max-w-3xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="sdvr-detail-modal">
              <div className="sticky top-0 bg-[#0F1E35] border-b border-[#1E3A5F] p-4 flex items-center justify-between z-10">
                <div>
                  <h2 className="text-white font-bold text-lg" style={{ fontFamily: "'Syne', sans-serif" }}>{selectedReport.report_id}</h2>
                  <div className="flex items-center gap-3 mt-1">
                    <StatusBadge status={selectedReport.status} />
                    <span className="text-slate-400 text-xs">Validation: {selectedReport.validation_id}</span>
                  </div>
                </div>
                <button onClick={() => setSelectedReport(null)} className="text-slate-400 hover:text-white p-1">
                  <XCircle className="w-5 h-5" />
                </button>
              </div>

              <div className="p-4 space-y-4">
                {/* Metadata */}
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div className="bg-[#132340] p-3 rounded-[3px] border border-[#1E3A5F]">
                    <span className="text-slate-500 block mb-1">Analysis ID</span>
                    <span className="text-white font-mono">{selectedReport.analysis_id}</span>
                  </div>
                  <div className="bg-[#132340] p-3 rounded-[3px] border border-[#1E3A5F]">
                    <span className="text-slate-500 block mb-1">Generated</span>
                    <span className="text-white">{new Date(selectedReport.generated_at).toLocaleString()}</span>
                  </div>
                  <div className="bg-[#132340] p-3 rounded-[3px] border border-[#1E3A5F] col-span-2">
                    <span className="text-slate-500 block mb-1">SHA-256 Fingerprint</span>
                    <span className="text-[#00C8E0] font-mono text-xs break-all">{selectedReport.raw_data_hash}</span>
                  </div>
                </div>

                {/* Verification Summary */}
                {selectedReport.verification_summary && (
                  <div>
                    <button
                      onClick={() => setExpandedVolume(expandedVolume === "verif" ? null : "verif")}
                      className="flex items-center gap-2 w-full text-left py-2 text-white font-semibold text-sm"
                    >
                      {expandedVolume === "verif" ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      <Fingerprint className="w-4 h-4 text-[#00C8E0]" />
                      Vol III: Algorithm Verification Results
                    </button>
                    {expandedVolume === "verif" && (
                      <div className="space-y-2 ml-6">
                        {selectedReport.verification_summary.results?.map((r, i) => (
                          <div key={i} className={`p-2 rounded-[3px] border text-xs ${r.overall_pass ? "bg-emerald-500/5 border-emerald-500/20" : "bg-red-500/5 border-red-500/20"}`}>
                            <div className="flex justify-between">
                              <span className="text-slate-300">{r.description}</span>
                              <span className={`font-bold ${r.overall_pass ? "text-emerald-400" : "text-red-400"}`}>{r.overall_pass ? "PASS" : "FAIL"}</span>
                            </div>
                            <div className="text-slate-500 mt-1">
                              Mean var: {r.mean.variance_pct.toFixed(8)}% | StdDev var: {r.std_dev.variance_pct.toFixed(8)}%
                              {r.cpk && ` | Cpk var: ${r.cpk.variance_pct.toFixed(8)}%`}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Signatures */}
                <div>
                  <h4 className="text-white font-semibold text-sm mb-2 flex items-center gap-2">
                    <PenTool className="w-4 h-4 text-[#00C8E0]" /> E-Signatures
                  </h4>
                  {selectedReport.signatures?.length > 0 ? (
                    <div className="space-y-2">
                      {selectedReport.signatures.map((sig, i) => (
                        <div key={i} className="bg-[#132340] p-3 rounded-[3px] border border-emerald-500/20 text-xs">
                          <div className="flex justify-between">
                            <span className="text-white font-medium">{sig.name} ({sig.role})</span>
                            <span className="text-slate-400">{new Date(sig.signed_at).toLocaleString()}</span>
                          </div>
                          <div className="text-slate-500 mt-1">{sig.meaning}</div>
                          <div className="text-[#00C8E0] font-mono text-xs mt-1 truncate">SHA-256: {sig.fingerprint}</div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-slate-500 text-xs">No signatures yet.</p>
                  )}

                  {/* Sign Form */}
                  {!selectedReport.report_locked && (
                    <div className="mt-3 p-3 bg-[#0A1628] border border-[#1E3A5F] rounded-[3px]">
                      <label className="text-slate-400 text-xs block mb-1">Meaning of Signature</label>
                      <input
                        data-testid="sign-meaning-input"
                        type="text"
                        value={signMeaning}
                        onChange={(e) => setSignMeaning(e.target.value)}
                        className="w-full bg-[#0F1E35] border border-[#1E3A5F] text-white text-xs p-2 rounded-[3px] focus:border-[#00C8E0] outline-none"
                      />
                      <button
                        data-testid="apply-signature-btn"
                        onClick={() => signReport(selectedReport.report_id)}
                        disabled={signing || selectedReport.report_locked}
                        className="mt-2 flex items-center gap-1.5 px-4 py-2 bg-emerald-600 text-white text-xs font-bold uppercase tracking-wide rounded-[3px] hover:bg-emerald-500 transition-colors disabled:opacity-50"
                      >
                        {signing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PenTool className="w-3.5 h-3.5" />}
                        Apply Electronic Signature
                      </button>
                    </div>
                  )}

                  {selectedReport.report_locked && (
                    <div className="mt-3 p-3 bg-red-500/5 border border-red-500/20 rounded-[3px] flex items-center gap-2">
                      <Lock className="w-4 h-4 text-red-400" />
                      <span className="text-red-400 text-xs font-medium">
                        SIGNATURE BLOCKED: Algorithm Verification FAILED. Math variance exceeds 0.0001% NIST threshold.
                      </span>
                    </div>
                  )}
                </div>

                {/* Download */}
                {selectedReport.pdf_generated && (
                  <button
                    data-testid="download-pdf-btn"
                    onClick={() => downloadPDF(selectedReport.report_id)}
                    className="w-full flex items-center justify-center gap-2 py-3 bg-[#1A6BCC] text-white text-sm font-bold uppercase tracking-wide rounded-[3px] hover:shadow-[0_0_20px_rgba(0,200,224,0.3)] transition-all"
                  >
                    <Download className="w-4 h-4" /> Download SDVR PDF ({Math.round((selectedReport.pdf_size_bytes || 0) / 1024)} KB)
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </QMSLayout>
  );
};

export default SDVRPage;
